import { useCallback } from 'react';

import { LinkPreview } from 'types/common';

import { UnuploadedAttachment } from '../config/types/Attachment';
import { useChatMessageSchedulingContext } from '../context/chatMessageScheduling';
import { useChatMessagesContext } from '../context/chatMessages';
import { useClearChatForm } from './useClearChatForm';
import { useShowScheduledModal } from './useShowScheduledModal';

type UseChatFormSubmitProps = {
  isTextMode: boolean;
  pauseRecording: () => void;
  stopRecording: () => Promise<UnuploadedAttachment & { duration: number }>;
  cancelRecording: () => void;
  getRecordedAttachment: () => UnuploadedAttachment;
};

export const useChatFormSubmit = ({
  isTextMode,
  pauseRecording,
  stopRecording,
  getRecordedAttachment,
  cancelRecording,
}: UseChatFormSubmitProps) => {
  const showSchedulingModal = useShowScheduledModal();
  const clearChatForm = useClearChatForm();
  const {
    sendMessage,
    sendMessageWithAttachments,
    parentMessage,
    editedMessage,
  } = useChatMessagesContext();
  const {
    isScheduledListVisible: isSchedulingMode,
    createScheduledMessage,
    createScheduledAttachmentMessage,
    editScheduledMessage,
  } = useChatMessageSchedulingContext();

  const onTextSubmit = useCallback(
    (text: string, linkPreview?: LinkPreview) => {
      sendMessage(text, linkPreview);
      clearChatForm();
    },
    [sendMessage, clearChatForm]
  );

  const onScheduledAudioRecordingSubmit = useCallback(async () => {
    pauseRecording();

    showSchedulingModal((scheduledFor) => {
      const attachment = getRecordedAttachment();
      cancelRecording();
      createScheduledAttachmentMessage({
        text: '',
        scheduledFor,
        attachments: [attachment],
        parentMessage,
      });
    });
  }, [
    pauseRecording,
    showSchedulingModal,
    cancelRecording,
    parentMessage,
    createScheduledAttachmentMessage,
    getRecordedAttachment,
  ]);

  const onAudioRecordingSubmit = useCallback(async () => {
    const attachment = await stopRecording();
    sendMessageWithAttachments({ text: '', attachments: [attachment] });
  }, [stopRecording, sendMessageWithAttachments]);

  const onScheduledTextSubmitWithScheduledModal = useCallback(
    (text: string, linkPreview?: LinkPreview) => {
      showSchedulingModal((scheduledFor) => {
        createScheduledMessage({
          text,
          scheduledFor,
          attachments: [],
          parentMessage,
          linkPreview,
        });
        clearChatForm();
      });
    },
    [showSchedulingModal, createScheduledMessage, clearChatForm, parentMessage]
  );

  const onScheduledMessageEdit = useCallback(
    (text: string, linkPreview?: LinkPreview) => {
      editScheduledMessage(text, editedMessage, linkPreview);
      clearChatForm();
    },
    [editScheduledMessage, clearChatForm, editedMessage]
  );

  const onScheduledTextSubmit = useCallback(
    (text: string, scheduledFor: string, linkPreview?: LinkPreview) => {
      createScheduledMessage({
        text,
        scheduledFor,
        attachments: [],
        parentMessage,
        linkPreview,
      });
      clearChatForm();
    },
    [clearChatForm, parentMessage, createScheduledMessage]
  );

  const onScheduledTextSubmitHandler = editedMessage
    ? onScheduledMessageEdit
    : onScheduledTextSubmitWithScheduledModal;

  const regularChatMessageSubmit = isTextMode
    ? onTextSubmit
    : onAudioRecordingSubmit;

  const scheduledChatMessageSubmit = isTextMode
    ? onScheduledTextSubmitHandler
    : onScheduledAudioRecordingSubmit;

  const onFormSubmit = isSchedulingMode
    ? scheduledChatMessageSubmit
    : regularChatMessageSubmit;

  return {
    onFormSubmit,
    onScheduledTextSubmit,
    onScheduledAudioRecordingSubmit,
  };
};
